/**
 * PricingPage.tsx — /#/pricing
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'

const NAV: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
  background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(201,168,76,0.15)',
  display: 'flex', alignItems: 'center', padding: '0 32px', height: 60,
  fontFamily: "'Segoe UI', system-ui, sans-serif",
}

const tiers = [
  { name: 'Shift',        monthlyPrice: 49.99,  desc: 'Essential shift management for small teams.',       highlight: false, available: true,  badge: null },
  { name: 'Terrain+NFC',  monthlyPrice: 149.99, desc: 'Adds terrain mapping and NFC checkpoints.',         highlight: true,  available: false, badge: 'MOST POPULAR' },
  { name: 'Command',      monthlyPrice: 399.99, desc: 'Full enterprise platform for large-scale ops.',     highlight: false, available: false, badge: null },
]

const sections: { title: string; rows: [string, boolean, boolean, boolean][] }[] = [
  { title: '📋 Shift Management', rows: [
    ['Daily posting board', true, true, true],
    ['Officer-to-post assignment', true, true, true],
    ['Break scheduling + rotation', true, true, true],
    ['Supervisor + officer roles', true, true, true],
    ['Shift notes + special details', true, true, true],
    ['Shift archive + history', true, true, true],
  ]},
  { title: '📻 Equipment Tracking', rows: [
    ['Radio assignment + OOS tracking', true, true, true],
    ['Security key tracking', true, true, true],
    ['Body recorder tracking', true, true, true],
    ['Access badge tracking', true, true, true],
  ]},
  { title: '☁️ Cloud & Mobile', rows: [
    ['Real-time cloud sync', true, true, true],
    ['Multi-device support', true, true, true],
    ['Android native app', true, true, true],
    ['Shift + break end notifications', true, true, true],
    ['Windows desktop app', true, true, true],
  ]},
  { title: '🔒 Security', rows: [
    ['JWT authentication', true, true, true],
    ['Role-based access control', true, true, true],
    ['Audit log', true, true, true],
    ['Password reset via email', true, true, true],
  ]},
  { title: '🗺️ Terrain + NFC', rows: [
    ['Zone terrain map', false, true, true],
    ['NFC checkpoint scanning', false, true, true],
    ['Incident reports', false, true, true],
    ['Priority support', false, true, true],
  ]},
  { title: '🖥️ Command + Enterprise', rows: [
    ['Command dashboard', false, false, true],
    ['Multi-site management', false, false, true],
    ['REST API access', false, false, true],
    ['Camera / access control integration', false, false, true],
    ['Priority SLA + 24/7 support', false, false, true],
  ]},
]

const Check = () => <span style={{ color: '#4ade80', fontSize: 18 }}>✓</span>
const Cross = () => <span style={{ color: '#1e293b', fontSize: 18 }}>—</span>

function fmt(monthly: number, annual: boolean) {
  const n = annual ? (monthly * 12 * 0.8) / 12 : monthly
  return '$' + n.toFixed(2)
}

export default function PricingPage() {
  const navigate = useNavigate()
  const [annual, setAnnual] = useState(false)

  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>

      <nav style={NAV}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <span onClick={() => navigate('/features')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Features</span>
          <span onClick={() => navigate('/find-agency')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Sign in</span>
          <button onClick={() => navigate('/signup')} style={{ padding: '8px 20px', borderRadius: 8, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>Get started</button>
        </div>
      </nav>

      <div style={{ textAlign: 'center', padding: '120px 24px 48px' }}>
        <div style={{ display: 'inline-block', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '.1em', textTransform: 'uppercase' as const, marginBottom: 20 }}>Pricing</div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800, color: '#f1f5f9', marginBottom: 16, lineHeight: 1.1 }}>Simple, transparent pricing.</h1>
        <p style={{ color: '#64748b', fontSize: 18, maxWidth: 500, margin: '0 auto 32px' }}>Start free on Shift. No credit card required.</p>

        {/* Billing toggle */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, background: '#0f1a2e', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 30, padding: '6px 8px' }}>
          <button onClick={() => setAnnual(false)} style={{ padding: '7px 20px', borderRadius: 24, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', background: !annual ? GOLD : 'transparent', color: !annual ? NAVY : '#64748b' }}>Monthly</button>
          <button onClick={() => setAnnual(true)}  style={{ padding: '7px 20px', borderRadius: 24, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', background: annual ? GOLD : 'transparent', color: annual ? NAVY : '#64748b' }}>
            Annual
            {!annual && <span style={{ marginLeft: 6, fontSize: 10, background: 'rgba(74,222,128,0.15)', color: '#4ade80', padding: '2px 6px', borderRadius: 10, fontWeight: 700 }}>Save 20%</span>}
          </button>
        </div>
        {annual && <div style={{ color: '#4ade80', fontSize: 13, marginTop: 10 }}>20% off — billed annually</div>}
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 80px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' as const }}>
          <thead>
            <tr>
              <th style={{ width: '34%', padding: '0 0 24px', textAlign: 'left' as const }}></th>
              {tiers.map(t => (
                <th key={t.name} style={{ width: '22%', padding: '0 12px 24px', textAlign: 'center' as const, verticalAlign: 'bottom' as const }}>
                  <div style={{ background: '#0f1a2e', border: `${t.highlight ? 2 : 1}px solid ${t.highlight ? GOLD : 'rgba(201,168,76,0.15)'}`, borderRadius: 12, padding: '20px 12px 0', position: 'relative' as const }}>
                    {t.badge && <div style={{ position: 'absolute' as const, top: -13, left: '50%', transform: 'translateX(-50%)', background: GOLD, color: NAVY, fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' as const }}>{t.badge}</div>}
                    {!t.available && <div style={{ position: 'absolute' as const, top: 10, right: 10, background: '#1e293b', color: '#475569', fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>COMING SOON</div>}
                    <div style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: 'uppercase' as const, letterSpacing: '.05em', marginBottom: 6 }}>{t.name}</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: '#f1f5f9', lineHeight: 1 }}>
                      {fmt(t.monthlyPrice, annual)}<span style={{ fontSize: 13, color: '#64748b', fontWeight: 400 }}>/mo</span>
                    </div>
                    {annual && <div style={{ fontSize: 11, color: '#4ade80', marginTop: 3 }}>billed ${(t.monthlyPrice * 12 * 0.8).toFixed(0)}/yr</div>}
                    <div style={{ fontSize: 12, color: '#475569', margin: '8px 0 16px', lineHeight: 1.5 }}>{t.desc}</div>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '14px 0' }}>
                      {t.available
                        ? <button onClick={() => navigate('/signup')} style={{ width: '100%', padding: '10px 0', borderRadius: 7, background: t.highlight ? GOLD : 'rgba(201,168,76,0.15)', color: t.highlight ? NAVY : GOLD, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>Apply →</button>
                        : <button disabled style={{ width: '100%', padding: '10px 0', borderRadius: 7, background: '#1e293b', color: '#475569', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'not-allowed' }}>Coming soon</button>
                      }
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map(section => (
              <>
                <tr key={section.title}>
                  <td colSpan={4} style={{ padding: '24px 0 8px', color: GOLD, fontWeight: 700, fontSize: 12, textTransform: 'uppercase' as const, letterSpacing: '.08em' }}>{section.title}</td>
                </tr>
                {section.rows.map(([feature, s, t, c], i) => (
                  <tr key={feature} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                    <td style={{ padding: '10px 8px', fontSize: 13, color: '#94a3b8' }}>{feature}</td>
                    <td style={{ textAlign: 'center' as const, padding: '10px 8px' }}>{s ? <Check /> : <Cross />}</td>
                    <td style={{ textAlign: 'center' as const, padding: '10px 8px' }}>{t ? <Check /> : <Cross />}</td>
                    <td style={{ textAlign: 'center' as const, padding: '10px 8px' }}>{c ? <Check /> : <Cross />}</td>
                  </tr>
                ))}
              </>
            ))}
            <tr>
              <td style={{ padding: '32px 0 0' }}></td>
              {tiers.map(t => (
                <td key={t.name} style={{ padding: '32px 12px 0', textAlign: 'center' as const }}>
                  {t.available
                    ? <button onClick={() => navigate('/signup')} style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: t.highlight ? GOLD : 'rgba(201,168,76,0.15)', color: t.highlight ? NAVY : GOLD, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>Apply →</button>
                    : <button disabled style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: '#1e293b', color: '#475569', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'not-allowed' }}>Coming soon</button>
                  }
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <p style={{ textAlign: 'center' as const, color: '#334155', fontSize: 13, marginTop: 32 }}>
          14-day free trial on Shift · No credit card required · Cancel anytime
        </p>
      </div>

      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: GOLD, letterSpacing: 2 }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' as const }}>
          {[['Features', '/features'], ['Docs', '/docs'], ['Download', '/download'], ['Privacy', '/privacy'], ['Terms', '/terms'], ['Sign in', '/find-agency']].map(([l, h]) => (
            <span key={l} onClick={() => navigate(h)} style={{ color: '#475569', cursor: 'pointer', fontSize: 13 }}>{l}</span>
          ))}
        </div>
        <div style={{ color: '#334155', fontSize: 12 }}>© 2026 Castellan Security</div>
      </footer>
    </div>
  )
}
